import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { MenuIcon, X, ChevronDown, Megaphone, UserRound } from "lucide-react";
import logo from "../../assets/images/logo.png";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { authService } from "../../services/authService";

export default function Navbar() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [isVisible, setIsVisible] = useState(true);
    const [isAtTop, setIsAtTop] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const location = useLocation();

    // Logic: Hide on scroll down, show on scroll up
    useEffect(() => {
        const controlNavbar = () => {
            const currentScrollY = window.scrollY;
            if (isMenuOpen) return;
            if (currentScrollY < 0) return;

            if (currentScrollY < 10) {
                setIsVisible(true);
                setIsAtTop(true);
            } else {
                setIsAtTop(false);
                if (currentScrollY > lastScrollY && currentScrollY > 100) {
                    setIsVisible(false);
                } else {
                    setIsVisible(true);
                }
            }
            setLastScrollY(currentScrollY);
        };

        window.addEventListener("scroll", controlNavbar);
        return () => window.removeEventListener("scroll", controlNavbar);
    }, [lastScrollY, isMenuOpen]);

    useEffect(() => {
        document.body.style.overflow = isMenuOpen ? "hidden" : "unset";
    }, [isMenuOpen]);

    useEffect(() => {
        setIsMenuOpen(false);
    }, [location.pathname]);

    const navbarData = [
        { id: "home", label: "nav.home", link: "/" },
        { id: "about", label: "nav.about", link: "/about" },
        { id: "packages", label: "nav.packages", link: "/packages" },
        { id: "services", label: "nav.services", link: "/services" },
        { id: "accommodations", label: "nav.accommodations", link: "/accommodations" },
        { id: "faq", label: "nav.faq", link: "/faq" },
        { id: "contact", label: "nav.contact", link: "/contact" }
    ];
    const announcementsItem = { id: "announcements", label: "nav.announcements", link: "/announcements" };

    return (
        <>
            <header
                className={`fixed top-0 left-0 w-full z-40 transition-all duration-500 ease-in-out px-4 lg:px-6
                    ${isVisible ? "translate-y-0" : "-translate-y-full"} 
                    ${isAtTop ? "bg-transparent" : "bg-white/80 backdrop-blur-xl shadow-md py-1"}
                `}
            >
                <div className="mx-auto flex max-w-[1440px] items-center justify-between">
                    <Link to="/" className="relative z-50 shrink-0">
                        <img src={logo} alt="Logo" className="w-20 md:w-24 lg:w-[120px]" />
                    </Link>

                    {/* Desktop Links & Lang Switcher */}
                    <div className="hidden xl:flex min-w-0 items-center gap-3 lg:gap-6">
                        <nav className="flex min-w-0 items-center gap-3 lg:gap-6">
                            {navbarData.map((item) => (
                                <Link
                                    key={item.id}
                                    to={item.link}
                                    className={`group relative whitespace-nowrap text-xs font-semibold tracking-wide transition-all duration-300 lg:text-sm
                                        ${location.pathname === item.link ? "text-orange" : "text-black-metal hover:text-orange"}
                                    `}
                                >
                                    {t(item.label)}
                                    <span className={`absolute -bottom-1 left-0 h-[2px] bg-orange transition-all duration-300 
                                        ${location.pathname === item.link ? "w-full" : "w-0 group-hover:w-full"}`}
                                    />
                                </Link>
                            ))}
                        </nav>
                        
                        {/* Language Switcher Desktop */}
                        <LanguageSwitcher />

                        <Link
                            to={announcementsItem.link}
                            aria-label={t(announcementsItem.label)}
                            className={`flex shrink-0 items-center gap-2 rounded-full px-3 py-2.5 text-xs font-bold transition-colors 2xl:px-4 2xl:text-sm
                                ${location.pathname === announcementsItem.link
                                    ? "bg-orange text-white"
                                    : "bg-reddish-orange text-orange hover:bg-orange hover:text-white"}`}
                        >
                            <Megaphone size={17} aria-hidden="true" />
                            <span className="hidden 2xl:inline">{t(announcementsItem.label)}</span>
                        </Link>

                        {/* Auth entry */}
                        <Link
                            to={user ? "/profile" : "/login"}
                            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-all duration-300
                                ${location.pathname === "/profile"
                                    ? "bg-orange text-white shadow-lg shadow-orange/20"
                                    : "bg-black-metal text-white hover:bg-orange hover:shadow-lg hover:shadow-orange/20"}
                            `}
                        >
                            <UserRound size={16} aria-hidden="true" />
                            {user ? t("nav.profile", "My Profile") : t("nav.login", "Sign In")}
                        </Link>
                    </div>

                    {/* Mobile Toggle */}
                    <div className="flex items-center gap-2 sm:gap-3 xl:hidden">
                        <LanguageSwitcher />
                        <Link
                            to={announcementsItem.link}
                            aria-label={t(announcementsItem.label)}
                            className={`flex shrink-0 items-center rounded-full p-2.5 transition-colors
                                ${location.pathname === announcementsItem.link
                                    ? "bg-orange text-white"
                                    : "bg-reddish-orange text-orange hover:bg-orange hover:text-white"}`}
                        >
                            <Megaphone size={18} aria-hidden="true" />
                        </Link>
                        <Link
                            to={user ? "/profile" : "/login"}
                            aria-label={user ? t("nav.profile", "My Profile") : t("nav.login", "Sign In")}
                            className={`flex shrink-0 items-center gap-2 rounded-full px-3 py-2 text-sm font-bold transition-colors sm:px-4
                                ${location.pathname === "/profile"
                                    ? "bg-orange text-white"
                                    : "bg-black-metal text-white hover:bg-orange"}`}
                        >
                            <UserRound size={17} aria-hidden="true" />
                            <span className="hidden md:inline">
                                {user ? t("nav.profile", "My Profile") : t("nav.login", "Sign In")}
                            </span>
                        </Link>
                        <button
                            onClick={() => setIsMenuOpen(true)}
                            aria-label={t("nav.open_menu", "Open menu")}
                            className="p-2 text-black-metal focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-light-orange"
                        >
                            <MenuIcon size={28} aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </header>

            <MobileMenu
                isOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
                data={[
                    ...navbarData,
                    announcementsItem,
                    user
                        ? { id: "profile", label: "nav.profile", link: "/profile" }
                        : { id: "login", label: "nav.login", link: "/login" },
                ]}
                currentPath={location.pathname}
                t={t}
            />
        </>
    );
}

// Inline SVG flags as data URIs — self-contained, so the language switcher
// makes no request to a third-party CDN (was flagcdn.com; GDPR/CNIL).
const FLAG_GB = "data:image/svg+xml," + encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 30'>" +
    "<clipPath id='s'><path d='M0,0 v30 h60 v-30 z'/></clipPath>" +
    "<clipPath id='t'><path d='M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z'/></clipPath>" +
    "<g clip-path='url(#s)'>" +
    "<path d='M0,0 v30 h60 v-30 z' fill='#012169'/>" +
    "<path d='M0,0 L60,30 M60,0 L0,30' stroke='#fff' stroke-width='6'/>" +
    "<path d='M0,0 L60,30 M60,0 L0,30' clip-path='url(#t)' stroke='#C8102E' stroke-width='4'/>" +
    "<path d='M30,0 v30 M0,15 h60' stroke='#fff' stroke-width='10'/>" +
    "<path d='M30,0 v30 M0,15 h60' stroke='#C8102E' stroke-width='6'/>" +
    "</g></svg>"
);
const FLAG_FR = "data:image/svg+xml," + encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 3 2'>" +
    "<rect width='3' height='2' fill='#fff'/>" +
    "<rect width='1' height='2' fill='#002395'/>" +
    "<rect x='2' width='1' height='2' fill='#ED2939'/></svg>"
);

function LanguageSwitcher() {
    const { i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const languages = [
        { code: "en", label: "EN", flag: FLAG_GB },
        { code: "fr", label: "FR", flag: FLAG_FR }
    ];

    const currentLanguage = languages.find(l => l.code === i18n.language) || languages[0];

    const toggleLanguage = (code) => {
        i18n.changeLanguage(code);
        setIsOpen(false);
        // Persist it on the account (no-op for guests) so the emails we send
        // later are in the same language as the interface.
        authService.setLanguage(code);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Change language"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100/30 transition-colors duration-200"
            >
                <img src={currentLanguage.flag} alt={currentLanguage.label} className="h-4 w-5 rounded-sm shadow-sm" />
                <span className="text-sm font-bold text-black-metal">{currentLanguage.label}</span>
                <ChevronDown size={14} className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden py-1 z-[60]">
                    {languages.map((lang) => (
                        <button
                            key={lang.code}
                            onClick={() => toggleLanguage(lang.code)}
                            className={`flex items-center gap-3 w-full px-4 py-3 text-sm transition-colors
                                ${i18n.language === lang.code ? "bg-gray-50 text-orange font-bold" : "text-gray-700 hover:bg-gray-50"}
                            `}
                        >
                            <img src={lang.flag} alt={lang.label} className="w-5 h-4 rounded-sm" />
                            {lang.label === 'EN' ? 'English' : 'Français'}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function MobileMenu({ isOpen, onClose, data, currentPath, t }) {
    return (
        <div className={`fixed inset-0 z-[100] overflow-hidden transition-all duration-300 ${isOpen ? "visible" : "invisible delay-300"}`}>
            <div 
                className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"}`} 
                onClick={onClose} 
            />
            <div className={`
                absolute top-0 right-0 h-full w-[80%] max-w-[320px] bg-white shadow-2xl 
                flex flex-col overflow-hidden will-change-transform transition-all duration-300 ease-in-out
                ${isOpen ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}
            `}>
                <div className="flex justify-end p-6">
                    <button onClick={onClose} aria-label={t("nav.close_menu", "Close menu")} className="p-2 text-gray-500 hover:text-black">
                        <X size={32} aria-hidden="true" />
                    </button>
                </div>
                
                <nav className={`flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-6 pb-8 transition-opacity duration-200 ${isOpen ? "opacity-100 delay-150" : "opacity-0"}`}>
                    {data.map((item) => (
                        <Link
                            key={item.id}
                            to={item.link}
                            onClick={onClose}
                            className={`py-4 px-5 rounded-2xl text-xl font-semibold transition-all
                                ${currentPath === item.link 
                                    ? "bg-orange text-white shadow-lg shadow-orange/20" 
                                    : "text-gray-700 hover:bg-gray-100"}
                            `}
                        >
                            {t(item.label)}
                        </Link>
                    ))}
                </nav>
            </div>
        </div>
    );
}
